import {
  EMPTY_GENERATED_FILES_SELECTION,
  GENERATED_FILES_ARCHIVE_DEFAULT_ERROR_KEY,
  GENERATED_FILES_ARCHIVE_ERROR_KEY_BY_STATUS,
  GENERATED_FILES_ARCHIVE_FILE_NAME_PREFIX,
  GENERATED_FILES_DEFAULT_LIMIT,
  GENERATED_FILES_ROWS_PER_PAGE_OPTIONS,
} from "../constants/generated-files";
import type {
  IBuildGeneratedFilesArchiveFileNameProps,
  IGeneratedFile,
  IGeneratedFilesArchiveQueryParams,
  IGeneratedFilesEmptyStateKeys,
  IGeneratedFilesPickerState,
  IGeneratedFilesQueryParams,
  IGeneratedFilesSelectionState,
  IGetGeneratedFilesParams,
  IIsGeneratedFilesArchiveDownloadProps,
  IReadGeneratedFilesSelectionProps,
  IResolveGeneratedFilesArchiveErrorKeyProps,
  IResolveGeneratedFilesDownloadLabelKeyProps,
  IResolveGeneratedFilesDownloadProps,
  IResolveGeneratedFilesPageSelectionStateProps,
  IResolvePickedGeneratedFilesProps,
  ISetGeneratedFilesPageInSelectionProps,
  ISetGeneratedFilesPageSelectionProps,
  IStartGeneratedFilesSelectionProps,
  IToggleGeneratedFileInSelectionProps,
  IToggleGeneratedFileSelectionProps,
  TGeneratedFilesArchiveErrorKey,
  TGeneratedFilesArchiveRequest,
  TGeneratedFilesDownload,
  TGeneratedFilesDownloadLabelKey,
  TGeneratedFileSource,
  TGeneratedFilesScope,
  TGeneratedFilesSelection,
  TGeneratedFilesSelectionCheckedState,
} from "../types/generated-files";
import {
  EGeneratedFilesArchiveScope,
  EGeneratedFilesDownloadKind,
} from "../types/generated-files";

export const getGeneratedFileSource = (
  file: IGeneratedFile,
): TGeneratedFileSource => {
  if (file.chatUuid) return "chat";
  if (file.cronRunUuid) return "agent";
  if (file.webhookRunUuid) return "webhook";
  if (file.hookRunUuid) return "eventRule";
  return "automation";
};

// NOTE: Turns the caller's filter state into the query string the server
// expects: an empty search and the `all` type carry no information, so they
// are dropped rather than sent as `search=` / `fileType=all`. One place for
// that rule, so web and mobile cannot disagree on what a request looks like.
export const buildGeneratedFilesQueryParams = (
  params: IGetGeneratedFilesParams = {},
): IGeneratedFilesQueryParams => {
  const {
    scope,
    search,
    chatUuid,
    fileType,
    fromDate,
    toDate,
    page = 1,
    limit = GENERATED_FILES_DEFAULT_LIMIT,
  } = params;

  return {
    // NOTE: NSAI-6501 — "mine" is the server default, so it is not sent.
    ...(scope === "shared" ? { scope } : {}),
    ...(search !== undefined && search.length > 0 ? { search } : {}),
    ...(chatUuid !== undefined && chatUuid.length > 0 ? { chatUuid } : {}),
    ...(fileType !== undefined && fileType !== "all" ? { fileType } : {}),
    ...(fromDate !== undefined ? { fromDate } : {}),
    ...(toDate !== undefined ? { toDate } : {}),
    page,
    limit,
  };
};

interface IIsGeneratedFilesFilterActiveProps {
  search: string;
  fileType: IGetGeneratedFilesParams["fileType"];
  fromDate?: string;
  toDate?: string;
}

// NOTE: Decides between the two empty states ("nothing yet" vs "nothing
// matches") — the same four inputs the request is built from.
export const isGeneratedFilesFilterActive = ({
  search,
  fileType,
  fromDate,
  toDate,
}: IIsGeneratedFilesFilterActiveProps): boolean =>
  search.length > 0 ||
  (fileType !== undefined && fileType !== "all") ||
  fromDate !== undefined ||
  toDate !== undefined;

interface IGetGeneratedFilesEmptyStateKeysProps {
  isFiltered: boolean;
  isSharedScope: boolean;
}

// NOTE: NSAI-6501 — three empty states: a filter narrowed the list to nothing
// (either tab), nobody has shared anything with the caller yet, or the caller
// has no files yet. A filter wins, since clearing it is the thing to do next.
export const getGeneratedFilesEmptyStateKeys = ({
  isFiltered,
  isSharedScope,
}: IGetGeneratedFilesEmptyStateKeysProps): IGeneratedFilesEmptyStateKeys => {
  if (isFiltered) {
    return {
      titleKey: "generated_files.empty_filtered",
      descriptionKey: "generated_files.empty_filtered_description",
    };
  }
  if (isSharedScope) {
    return {
      titleKey: "generated_files.empty_shared",
      descriptionKey: "generated_files.empty_shared_description",
    };
  }
  return {
    titleKey: "generated_files.empty",
    descriptionKey: "generated_files.empty_description",
  };
};

export const isGeneratedFilesRowsPerPageOption = (
  value: number,
): value is (typeof GENERATED_FILES_ROWS_PER_PAGE_OPTIONS)[number] =>
  GENERATED_FILES_ROWS_PER_PAGE_OPTIONS.some((option) => option === value);

// NOTE: NSAI-5312 — the query an archive download is requested with, built in
// one place so web (axios) and mobile (the native downloader, via
// resolveAuthorizedRequest) send byte-identical URLs. A selection is joined
// into ONE comma-separated value: axios would send an array as repeated
// `fileUuids[]=` keys, which Express 5's default query parser keeps under the
// literal name "fileUuids[]" and the server would never see.
export const buildGeneratedFilesArchiveQueryParams = (
  request: TGeneratedFilesArchiveRequest,
): IGeneratedFilesArchiveQueryParams => {
  if (request.scope === EGeneratedFilesArchiveScope.SELECTION) {
    return { fileUuids: request.fileUuids.join(",") };
  }

  if (request.scope === EGeneratedFilesArchiveScope.CHAT) {
    return { chatUuid: request.chatUuid };
  }

  return { runUuid: request.runUuid };
};

// NOTE: The user's local calendar date — the day they will look for the file
// under — not the UTC date the server stamps into Content-Disposition.
export const buildGeneratedFilesArchiveFileName = ({
  date,
}: IBuildGeneratedFilesArchiveFileNameProps): string => {
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${GENERATED_FILES_ARCHIVE_FILE_NAME_PREFIX}-${year}-${month}-${day}.zip`;
};

// NOTE: The server answers 404 both for a selected file that is no longer
// available and for a chat or run with no generated files — only the scope
// tells the two apart, and the second must not read "some files are gone".
export const resolveGeneratedFilesArchiveErrorKey = ({
  status,
  scope,
}: IResolveGeneratedFilesArchiveErrorKeyProps): TGeneratedFilesArchiveErrorKey => {
  if (status === 404 && scope !== EGeneratedFilesArchiveScope.SELECTION) {
    return "generated_files.archive.errors.no_files";
  }

  return (
    (status === undefined
      ? undefined
      : GENERATED_FILES_ARCHIVE_ERROR_KEY_BY_STATUS[status]) ??
    GENERATED_FILES_ARCHIVE_DEFAULT_ERROR_KEY
  );
};

// NOTE: NSAI-5312 — the file selection behind the bulk download, as pure map
// operations so web and mobile tick, untick and select-a-page identically.
// Each returns a new map; the caller's state is never mutated.
export const toggleGeneratedFileSelection = ({
  selectedFiles,
  file,
}: IToggleGeneratedFileSelectionProps): TGeneratedFilesSelection => {
  const next = new Map(selectedFiles);

  if (next.has(file.uuid)) {
    next.delete(file.uuid);
  } else {
    next.set(file.uuid, file);
  }

  return next;
};

// NOTE: Adds or removes only this page's files, so ticks made on other pages
// survive a select-all / clear-all here.
export const setGeneratedFilesPageSelection = ({
  selectedFiles,
  pageFiles,
  isSelected,
}: ISetGeneratedFilesPageSelectionProps): TGeneratedFilesSelection => {
  const next = new Map(selectedFiles);

  for (const file of pageFiles) {
    if (isSelected) {
      next.set(file.uuid, file);
    } else {
      next.delete(file.uuid);
    }
  }

  return next;
};

export const resolveGeneratedFilesPageSelectionState = ({
  selectedFiles,
  pageFiles,
}: IResolveGeneratedFilesPageSelectionStateProps): TGeneratedFilesSelectionCheckedState => {
  const selectedOnPageCount = pageFiles.filter((file) =>
    selectedFiles.has(file.uuid),
  ).length;

  if (pageFiles.length > 0 && selectedOnPageCount === pageFiles.length) {
    return true;
  }

  return selectedOnPageCount > 0 ? "indeterminate" : false;
};

// NOTE: NSAI-5312 — a chat's "Download generated files" list opens with every
// file ticked. Until the user changes a tick nothing is stored (`null`), so a
// refetch that brings a new file in ticks it too; afterwards it is the set
// they chose. Either way it is read through the current list, in its order,
// so a file that has since disappeared never rides along into the download.
export const resolvePickedGeneratedFiles = ({
  files,
  pickedFiles,
}: IResolvePickedGeneratedFilesProps): readonly IGeneratedFile[] =>
  pickedFiles === null
    ? files
    : files.filter((file) => pickedFiles.has(file.uuid));

// NOTE: NSAI-5312 — the chat picker's whole view of its list, derived at once
// from what is listed and what was picked, so web and mobile show the same
// ticks, count and select-all state.
export const resolveGeneratedFilesPickerState = ({
  files,
  pickedFiles,
}: IResolvePickedGeneratedFilesProps): IGeneratedFilesPickerState => {
  const selectedFiles = resolvePickedGeneratedFiles({ files, pickedFiles });
  const selection: TGeneratedFilesSelection = new Map(
    selectedFiles.map((file) => [file.uuid, file]),
  );

  return {
    selectedFiles,
    selection,
    checkedState: resolveGeneratedFilesPageSelectionState({
      selectedFiles: selection,
      pageFiles: files,
    }),
  };
};

// NOTE: NSAI-5312 — the AI Generated Files page's selection as state
// transitions, shared so both apps tick, untick and select a page the same
// way. A selection belongs to the filters it was made under: the page's hook
// starts a new one whenever its filter key changes.
export const startGeneratedFilesSelection = ({
  filterKey,
}: IStartGeneratedFilesSelectionProps): IGeneratedFilesSelectionState => ({
  filterKey,
  files: EMPTY_GENERATED_FILES_SELECTION,
});

// NOTE: Empty for a state made under other filters — the one render in which
// a filter has changed and the hook has not yet started the new selection.
export const readGeneratedFilesSelection = ({
  state,
  filterKey,
}: IReadGeneratedFilesSelectionProps): TGeneratedFilesSelection =>
  state.filterKey === filterKey ? state.files : EMPTY_GENERATED_FILES_SELECTION;

export const toggleGeneratedFileInSelection = ({
  state,
  file,
}: IToggleGeneratedFileInSelectionProps): IGeneratedFilesSelectionState => ({
  filterKey: state.filterKey,
  files: toggleGeneratedFileSelection({ selectedFiles: state.files, file }),
});

export const setGeneratedFilesPageInSelection = ({
  state,
  pageFiles,
  isSelected,
}: ISetGeneratedFilesPageInSelectionProps): IGeneratedFilesSelectionState => ({
  filterKey: state.filterKey,
  files: setGeneratedFilesPageSelection({
    selectedFiles: state.files,
    pageFiles,
    isSelected,
  }),
});

// NOTE: NSAI-5312 — THE one-file-or-ZIP rule: one picked file downloads as
// itself, two or more as one archive. The download itself, its label and its
// icon all read it, so a change to the threshold is made here once and both
// apps follow.
export const isGeneratedFilesArchiveDownload = ({
  selectedCount,
}: IIsGeneratedFilesArchiveDownloadProps): boolean => selectedCount > 1;

// NOTE: NSAI-5312 — one picked file downloads as itself, under its own name;
// two or more download as one archive of exactly the picked files (never a
// whole-chat archive, which could pick up a file generated after the list was
// shown). Null when nothing is picked.
export const resolveGeneratedFilesDownload = ({
  files,
}: IResolveGeneratedFilesDownloadProps): TGeneratedFilesDownload | null => {
  const [firstFile] = files;

  if (firstFile === undefined) {
    return null;
  }

  if (!isGeneratedFilesArchiveDownload({ selectedCount: files.length })) {
    return { kind: EGeneratedFilesDownloadKind.FILE, file: firstFile };
  }

  return {
    kind: EGeneratedFilesDownloadKind.ARCHIVE,
    request: {
      scope: EGeneratedFilesArchiveScope.SELECTION,
      fileUuids: files.map((file) => file.uuid),
    },
  };
};

// NOTE: The button says what the click will produce — the same rule as
// resolveGeneratedFilesDownload, so the label and the download never disagree.
export const resolveGeneratedFilesDownloadLabelKey = ({
  selectedCount,
}: IResolveGeneratedFilesDownloadLabelKeyProps): TGeneratedFilesDownloadLabelKey =>
  isGeneratedFilesArchiveDownload({ selectedCount })
    ? "generated_files.archive.download"
    : "generated_files.download";

interface IIsGeneratedFilesQueryOfScopeProps {
  queryKey: readonly unknown[];
  scope: TGeneratedFilesScope;
}

// NOTE: NSAI-6501 — both clients keep the previous page's rows on screen while
// the next one loads (search, filters, paging), but only within ONE scope.
// Switching between "My files" and "Shared with me" must not show the other
// tab's rows — with the wrong actions on them — under the new tab's heading,
// so a key of another scope falls back to the loading state instead.
export const isGeneratedFilesQueryOfScope = ({
  queryKey,
  scope,
}: IIsGeneratedFilesQueryOfScopeProps): boolean => {
  const keyParams = queryKey[1];
  if (typeof keyParams !== "object" || keyParams === null) return false;
  return "scope" in keyParams && keyParams.scope === scope;
};
